import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { fetchEmails, addEmail, updateEmail, removeEmail } from "../actions/emailThunks";
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import './EmailList.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  Search: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  X: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Trash: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  ),
  Plus: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  ChevronUp: ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15"/>
    </svg>
  ),
  ChevronDown: ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  ChevronsUpDown: ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="7 15 12 20 17 15"/><polyline points="7 9 12 4 17 9"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (d.getFullYear() < 2000) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

// ─── EDITABLE CELL ────────────────────────────────────────────────────────────
function EditableCell({ row, field, className, onSave }) {
  const [value, setValue] = useState(row[field]);
  const confirmed = useRef(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter')  { confirmed.current = true;  e.target.blur(); }
    if (e.key === 'Escape') { confirmed.current = false; setValue(row[field]); e.target.blur(); }
  };

  const handleBlur = () => {
    if (confirmed.current && value !== row[field]) onSave(value);
    else setValue(row[field]);
    confirmed.current = false;
  };

  return (
    <input
      className={`em-cell-input ${className}`}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      title="Press Enter to save, Escape to cancel"
    />
  );
}

// ─── SORT ICON ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <span className="em-sort-icon em-sort-idle"><Icon.ChevronsUpDown /></span>;
  return (
    <span className="em-sort-icon em-sort-active">
      {sortDir === 'asc' ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
    </span>
  );
}

// ─── CONFIRM MODAL ────────────────────────────────────────────────────────────
function ConfirmModal({ row, onClose, onConfirm }) {
  return (
    <div className="em-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="em-modal">
        <div className="em-modal-header">
          <div>
            <p className="em-modal-title">Delete entry</p>
            <p className="em-modal-subtitle">This action cannot be undone</p>
          </div>
          <button className="em-close-btn" onClick={onClose}><Icon.X size={16} /></button>
        </div>
        <div className="em-modal-body">
          <div className="em-modal-entry">
            <div className="em-modal-entry-row">
              <span className="em-modal-entry-label">Wrong email</span>
              <span className="em-modal-entry-value em-wrong">{row.wrongEmail || '—'}</span>
            </div>
            <div className="em-modal-entry-row">
              <span className="em-modal-entry-label">Right email</span>
              <span className="em-modal-entry-value em-right">{row.rightEmail || '—'}</span>
            </div>
          </div>
        </div>
        <div className="em-modal-footer">
          <button className="em-btn em-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="em-btn em-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function EmailList() {
  const classes  = useStyles();
  const dispatch = useDispatch();
  const userId   = useSelector(state => state.session.user.id);
  const { items, loading, error } = useSelector(state => state.emailsReducer);

  const [search,    setSearch]    = useState('');
  const [newWrong,  setNewWrong]  = useState('');
  const [newRight,  setNewRight]  = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir,   setSortDir]   = useState('asc');
  const [deleteRow, setDeleteRow] = useState(null);
  const rightRef = useRef(null);
  const wrongRef = useRef(null);

  useEffect(() => { dispatch(fetchEmails(userId)); }, []);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = items
    .filter((row) => {
      const q = search.toLowerCase();
      return row.wrongEmail?.toLowerCase().includes(q) || row.rightEmail?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      const vA = a[sortField] ?? '', vB = b[sortField] ?? '';
      const cmp = vA < vB ? -1 : vA > vB ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleUpdate = (item, field, value) => dispatch(updateEmail({ userId, ...item, [field]: value }));

  const handleAdd = () => {
    if (!newWrong.trim() && !newRight.trim()) return;
    dispatch(addEmail({ userId, wrongEmail: newWrong.trim(), rightEmail: newRight.trim() }));
    setNewWrong(''); setNewRight('');
    wrongRef.current?.focus();
  };

  const columns = [
    { field: 'creationDate', label: 'Creation Date', cls: 'em-th-date' },
    { field: 'creationUser', label: 'Creation User', cls: ''           },
    { field: 'wrongEmail',   label: 'Wrong Email',   cls: ''           },
    { field: 'rightEmail',   label: 'Right Email',   cls: ''           },
    { field: 'changeDate',   label: 'Change Date',   cls: 'em-th-date' },
    { field: 'changeUser',   label: 'Change User',   cls: ''           },
  ];

  return (
    <Page className={classes.root} title="Emails Wrong Right">
      <Header module="NASP" view="Emails Wrong Right" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="em-root">

            {deleteRow && (
              <ConfirmModal
                row={deleteRow}
                onClose={() => setDeleteRow(null)}
                onConfirm={() => { dispatch(removeEmail(userId, deleteRow.id)); setDeleteRow(null); }}
              />
            )}

            <div className="em-inner">

              {/* ── Card ──────────────────────────────────────────────────── */}
              <div className="em-card">
                <div className="em-card-header">
                  <span className="em-card-title">Email corrections</span>
                  <span className="em-badge">
                    {loading ? 'Loading...' : `${filtered.length} of ${items.length} ${items.length === 1 ? 'entry' : 'entries'}`}
                  </span>
                </div>

                <div className="em-card-body">

                  {error && <div className="em-error">⚠ {error}</div>}

                  {/* Search + Add */}
                  <div className="em-toolbar">
                    <div className="em-search-wrap">
                      <span className="em-search-icon"><Icon.Search color="#94a3b8" /></span>
                      <input
                        className="em-search-input"
                        type="text"
                        placeholder="Search by email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button className="em-search-clear" onClick={() => setSearch('')}>
                          <Icon.X color="#94a3b8" />
                        </button>
                      )}
                    </div>

                    <div className="em-add-row">
                      <input
                        ref={wrongRef}
                        className="em-add-input em-add-wrong"
                        placeholder="Wrong email..."
                        value={newWrong}
                        onChange={(e) => setNewWrong(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && rightRef.current?.focus()}
                        disabled={loading}
                      />
                      <input
                        ref={rightRef}
                        className="em-add-input em-add-right"
                        placeholder="Right email..."
                        value={newRight}
                        onChange={(e) => setNewRight(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        disabled={loading}
                      />
                      <button className="em-btn em-btn-primary" onClick={handleAdd} disabled={loading}>
                        <Icon.Plus color="#fff" /> Add
                      </button>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="em-table-wrap">
                    <table className="em-table">
                      <thead>
                        <tr>
                          {columns.map(({ field, label, cls }) => (
                            <th
                              key={field}
                              className={`em-th em-th-sortable ${cls}`}
                              onClick={() => handleSort(field)}
                            >
                              <span className="em-th-inner">
                                {label}
                                <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
                              </span>
                            </th>
                          ))}
                          <th className="em-th em-th-action" />
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((row, i) => (
                          <tr key={row.id} className="em-tr">
                            <td className="em-td em-td-date">{formatDate(row.creationDate)}</td>
                            <td className="em-td em-td-user">{row.creationUser || ''}</td>
                            <td className="em-td">
                              <EditableCell row={row} field="wrongEmail" className="em-wrong" onSave={(v) => handleUpdate(row, 'wrongEmail', v)} />
                            </td>
                            <td className="em-td">
                              <EditableCell row={row} field="rightEmail" className="em-right" onSave={(v) => handleUpdate(row, 'rightEmail', v)} />
                            </td>
                            <td className="em-td em-td-date">{formatDate(row.changeDate)}</td>
                            <td className="em-td em-td-user">{row.changeUser || ''}</td>
                            <td className="em-td em-td-action">
                              <button className="em-delete-btn" onClick={() => setDeleteRow(row)} disabled={loading}>
                                <Icon.Trash color="#dc2626" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!loading && filtered.length === 0 && (
                          <tr>
                            <td colSpan={7} className="em-empty">
                              {search ? `No results for "${search}"` : 'No entries found. Add one above.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}